import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  enrolMemberTelegram,
  getMemberEnrolment,
  getTeamMe,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  listMemberParties,
  listTeamMembers,
  type ChatEnrolmentStatus,
  type PackJobRole,
  type RecordRow,
  type TeamMember,
} from '../lib/api';
import { routePath } from '../routes/route';
import { useRecordsVersion } from '../lib/recordEditor';
import { activeOnly, splitRetired } from '../lib/retired';
import { AddTechPanel, InvitePanel } from '../components/TeamDoor';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import './Techs.css';

// S4 — THE TECHS LIST (wf02 §01b, techs()): the first surface where what
// you hold changes what you read. The sub-line and the ask-line are the
// prototype's own copy, verbatim. WHO HOLDS WHAT is decided by the SERVER
// on every write (the C5e gate converts what exceeds you into an ask) —
// the role read here (GET /api/team/me) only chooses which door to DRAW,
// never whether it opens.
//
// Today's count per tech is joined from the visit side: a per-TECH link
// read would truncate at the 50-link page (oldest first) and silently zero
// the very numbers that matter. The prototype's van and hours-this-week
// columns have no honest source yet (no vehicle field; a week-wide join is
// unbounded) — omitted, named in the chunk report.
//
// S16 — THE VISIT SIDE STOPS COSTING ONE REQUEST PER VISIT. S4 read each of
// today's visits on its own (`GET /records/:id?include=links`) and, past
// TODAY_JOIN_CAP (40), gave up and rendered '—' in every row — a cap that
// existed only because reads were expensive. The visits now arrive WITH
// their `tech_profile` link on the page they belong to (S15's
// `?include=links`), so the join is one request per 200 visits, the cap is
// deleted, and a busy day counts exactly like a quiet one.
//
// THE ABSENT-NUMBER STATE SURVIVES, re-anchored and split in two. It used
// to be one '—' meaning "more than forty visits today"; it is now
// "reading…" while the count is on its way and "not read" when the visit
// read did not come back — the only remaining reason this column cannot be
// trusted. Neither is a zero, and neither takes the screen down with it:
// the roster renders from its own read, because a refused count is not a
// refused team.
//
// S11 (Part B) — RETIRED TECHS LEAVE THE ROSTER. Huan's 2026-08-12 ruling
// turned Delete into Retire, and Retire only sets status 'archived' — which
// core's listRecords does not filter (it excludes 'deleted' alone), so a
// retired tech kept sitting in this list, unmarked. They now leave the
// roster and the count, and stay ONE TAP away behind their own line: "a
// tech who leaves keeps their history and their payroll" (wf02), so hiding
// them outright would be a different lie. The filter is client-side because
// GET /records has no status filter to ask for — see lib/retired.ts.
//
// S12b — THE INVITE DOOR LANDS HERE, and nowhere else. `POST /auth/invites`
// had zero callers in this app: the owner of the demo tenant held every
// authority the server recognises and could not invite one person, because
// no screen drew the door. Techs is the people surface the product already
// has and the blessed nav (wf02 §01b) declares no Team tab, so the door
// opens here rather than as a new rail destination.
//
// Two things follow from that choice. PENDING INVITES belong on this screen
// too — an invite that vanishes the moment it is sent has no answer to "did
// it send?", and the next thing that happens is it gets pressed twice. And
// the roster read that shows them (GET /api/team/members) is ADMIN-ONLY, so
// a 403 here means "no pending list for you", never a broken screen.
//
// The two doors are drawn for EVERYONE. Not an oversight: the server's gate
// is the wall (invite.create, minimumRole admin), and a client-side role
// check deciding whether the button is DRAWN is precisely how an owner came
// to see no option anywhere. Someone who may not invite gets the route's
// own refusal, rendered in its own words.
//
// S5d §3 — THE ENROLMENT DOOR. The only enrolment UI before this was
// founder-gated self-enrolment in first-run; connecting an EXISTING member
// to Telegram took SQL (which is how Huan's own enrolment happened during
// the 08-14 walk). The door lands on the roster rows here: per row, the
// live enrolment status and, where none exists, the connect action —
// posting at the SAME production route first-run's self-enrol already uses
// (the two-step in api.ts's enrolMemberTelegram, generalized, not
// duplicated). UNLIKE the invite door, this one IS presentation-gated to
// admin/owner (the role the frame already fetched): for a dispatcher the
// action converts into a DEAD-END ask (fileAsk available:false), and a
// button that 409s into nothing is a line that names something you cannot
// reach — the walk pattern three walks running. The row join is
// membership_parties (GET :id/parties) because a roster row is a RECORD and
// enrolment hangs off the membership bound to it.

export interface TechsProps {
  tenantId: string;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const TECH_TYPE = 'hvac_tech_profile';
const VISIT_TYPE = 'hvac_visit';
/** The one relationship this column renders. */
const VISIT_LINK_TYPES = ['tech_profile'];

/** Presentation only — the server's authority gate is the wall. */
export function canEditTechs(member: TeamMember): boolean {
  return (
    member.role === 'owner' ||
    member.role === 'admin' ||
    member.jobRoles.includes('admin') ||
    member.jobRoles.includes('owner')
  );
}

function visitWhen(v: RecordRow): string | null {
  const f = v.fields;
  const raw = f['actual_end'] ?? f['actual_start'] ?? f['scheduled_start'];
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

function isToday(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  );
}

function textField(r: RecordRow, name: string): string {
  const v = r.fields[name];
  return typeof v === 'string' ? v : '';
}

export function Techs({ tenantId, onNavigate }: TechsProps) {
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [member, setMember] = useState<TeamMember | null>(null);
  const [todayByTech, setTodayByTech] = useState<Map<string, number> | null>(null);
  /** Why the today column has no numbers, when it has none. Null when the
   *  count is real. */
  const [todayFailure, setTodayFailure] = useState<ReadFailure | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [showRetired, setShowRetired] = useState(false);
  const [packJobRoles, setPackJobRoles] = useState<PackJobRole[]>([]);
  // S5d §3 — the WHOLE membership list (the same admin-only read that used
  // to keep only the invited slice): the enrolment door needs the active
  // members too, because a roster row is a RECORD and the Telegram enrolment
  // hangs off the MEMBERSHIP bound to it.
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [door, setDoor] = useState<'none' | 'add' | 'invite'>('none');
  // S5d §3 — the enrolment join, read only for a viewer whose role can
  // complete the door (see the effect below). recordId -> the membership
  // bound to it; membershipId -> its live enrolment ('unread' when the read
  // itself failed — a missing answer must not render as "no enrolment").
  const [memberByRecord, setMemberByRecord] = useState<Map<string, TeamMember>>(new Map());
  const [enrolments, setEnrolments] = useState<Map<string, ChatEnrolmentStatus | null | 'unread'>>(
    new Map()
  );
  // The per-row connect panel: which record's door is open, and its draft.
  const [enrolOpenFor, setEnrolOpenFor] = useState<string | null>(null);
  const [enrolPhone, setEnrolPhone] = useState('');
  const [enrolBusy, setEnrolBusy] = useState(false);
  const [enrolError, setEnrolError] = useState<string | null>(null);
  // Bumped by either panel on a successful write, so the roster (and with
  // it the pending list) re-reads without the panel knowing what a roster is.
  const [teamVersion, setTeamVersion] = useState(0);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      // S12b: a REFRESH must not blank the screen. Adding a technician
      // bumps the records version, which re-runs this effect — and the
      // unconditional 'loading' that used to sit here replaced the whole
      // screen, taking the just-written receipt with it. A receipt that is
      // destroyed by the very success it reports is an acknowledgement, not
      // a receipt. The first load still says "One moment…"; every later one
      // swaps the rows underneath a screen that stays put.
      setLoad((prev) => (prev === 'ready' ? 'ready' : 'loading'));
      setFailure(null);
      try {
        const [techs, me] = await Promise.all([
          listAllRecordsByType(tenantId, TECH_TYPE),
          getTeamMe(tenantId),
        ]);
        if (cancelled) return;
        setRows(techs);
        setMember(me.member);
        setPackJobRoles(me.packJobRoles ?? []);
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
        return;
      }

      // THE TODAY COLUMN, on its own read and its own failure. One request
      // per 200 visits, links included — no cap, and a count that is exact
      // for every roster size. Its failure costs the COLUMN, never the team:
      // "we could not count today" and "nobody is working today" are
      // different sentences and only one of them is ever true here.
      setTodayFailure(null);
      try {
        const visitLinks = await listAllRecordsWithLinks(tenantId, VISIT_TYPE, {
          linkTypes: VISIT_LINK_TYPES,
        });
        if (cancelled) return;
        const unanswered = new Set([...visitLinks.unreadable, ...visitLinks.truncated]);
        const counts = new Map<string, number>();
        for (const v of activeOnly(visitLinks.records)) {
          const when = visitWhen(v);
          if (when === null || !isToday(when)) continue;
          if (unanswered.has(v.id)) continue;
          const links = visitLinks.linksByRecordId[v.id];
          if (links === undefined) continue;
          const techLink = links.find(
            (l) => l.link_type === 'tech_profile' && l.dst_type === TECH_TYPE
          );
          if (techLink) counts.set(techLink.dst_id, (counts.get(techLink.dst_id) ?? 0) + 1);
        }
        setTodayByTech(counts);
      } catch (err) {
        if (cancelled) return;
        setTodayByTech(null);
        setTodayFailure(describeReadFailure(err));
      }
    }
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, recordsVersion, retryNonce]);

  // The membership list, read on its own so a 403 costs nothing: the roster
  // is admin-only, and a member who cannot read it still gets the whole
  // screen (the pending lines and the enrolment door simply don't exist for
  // them — which for the door is also the §3 gate: it is drawn only for a
  // role that can complete it).
  useEffect(() => {
    let cancelled = false;
    listTeamMembers(tenantId)
      .then((all) => {
        if (!cancelled) setMembers(all);
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, teamVersion]);

  // S5d §3 — the enrolment join: which membership IS this roster row, and
  // does it have a live Telegram enrolment. Read only when the viewer's role
  // can complete the door (admin/owner — the role the frame already fetched;
  // a dispatcher gets no door and therefore no reads). Both reads are per
  // MEMBERSHIP, so the cost is bounded by the size of the team — never by
  // the shop (requestBudget.test.tsx pins this).
  useEffect(() => {
    if (!member || !canEditTechs(member) || members.length === 0) return;
    let cancelled = false;
    void (async () => {
      const byRecord = new Map<string, TeamMember>();
      const byMembership = new Map<string, ChatEnrolmentStatus | null | 'unread'>();
      await Promise.all(
        members
          .filter((m) => m.status === 'active' || m.status === 'invited')
          .map(async (m) => {
            try {
              const parties = await listMemberParties(tenantId, m.id);
              for (const p of parties) byRecord.set(p.partyRecordId, m);
            } catch {
              // No binding read -> no door on any row for this member. The
              // row stays a plain roster line rather than guessing a join.
            }
            if (m.status !== 'active') return; // enrol active members only
            try {
              byMembership.set(m.id, await getMemberEnrolment(tenantId, m.id));
            } catch {
              byMembership.set(m.id, 'unread');
            }
          })
      );
      if (cancelled) return;
      setMemberByRecord(byRecord);
      setEnrolments(byMembership);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, members, member]);

  // WHO THIS SCREEN HIRES (Huan, 2026-08-25: "it seems wrong to have it there
  // for Admin"). Techs is the CREW's screen, so it invites the crew — the
  // roles the pack SEATS as members. The office and dispatch are invited from
  // the owner's own preferences instead, where hiring your office belongs.
  //
  // Split on the pack's declared SEAT, never on role names: the web has no
  // business knowing that this pack calls its field people 'tech', and a pack
  // that adds a second field role gets it here for free.
  const crewRoles = packJobRoles.filter((r) => r.seat === 'member');

  const pending = members.filter((m) => m.status === 'invited');

  async function submitEnrol(membershipId: string): Promise<void> {
    const phone = enrolPhone.trim();
    if (phone.length === 0 || enrolBusy) return;
    setEnrolBusy(true);
    setEnrolError(null);
    try {
      const enrolment = await enrolMemberTelegram(tenantId, membershipId, phone);
      setEnrolments((prev) => new Map(prev).set(membershipId, enrolment));
      setEnrolOpenFor(null);
      setEnrolPhone('');
    } catch (err) {
      // The server's own refusal, verbatim — its E.164 message and its
      // one-live-enrolment message are both more precise than anything this
      // screen could invent.
      setEnrolError(err instanceof ApiError ? err.message : 'Could not reach the server.');
    } finally {
      setEnrolBusy(false);
    }
  }

  const crumb = (
    <nav class="techs__crumb" aria-label="Breadcrumb">
      <button type="button" onClick={() => onNavigate(routePath({ screen: 'today' }))}>
        Ether Inc
      </button>
      <span class="techs__crumb-sep" aria-hidden="true">
        ›
      </span>
      <span class="techs__crumb-cur">Techs</span>
    </nav>
  );

  if (load === 'loading') {
    return (
      <div class="techs">
        {crumb}
        <div class="techs__eyebrow">Techs</div>
        <div class="techs__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    return (
      <div class="techs">
        {crumb}
        <div class="techs__eyebrow">Techs</div>
        <div class="techs__say">
          {failure.headline}
          <br />
          <span class="techs__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="techs__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!member) return null;

  const editable = canEditTechs(member);
  const { active, retired } = splitRetired(rows);

  return (
    <div class="techs">
      {crumb}
      <div class="techs__head">
        <div>
          <div class="techs__eyebrow">
            Team · {active.length === 1 ? '1 technician' : `${active.length} technicians`}
          </div>
          <h2 class="techs__title">Techs</h2>
          <div class="techs__sub">
            {editable
              ? 'You can add, edit and deactivate technicians.'
              : "You can see and schedule technicians. Their details are the admin's to change."}
          </div>
        </div>
        <div class="techs__doors">
          {/* The RECORD door keeps S4's presentation gate — unchanged. The
              INVITE door below does not: see this file's header. */}
          {editable ? (
            <button
              type="button"
              class="techs__add"
              data-testid="techs-add-open"
              onClick={() => setDoor((d) => (d === 'add' ? 'none' : 'add'))}
            >
              + New technician
            </button>
          ) : null}
          <button
            type="button"
            class="techs__add"
            data-testid="techs-invite-open"
            onClick={() => setDoor((d) => (d === 'invite' ? 'none' : 'invite'))}
          >
            Invite someone
          </button>
        </div>
      </div>

      {door === 'add' ? (
        <AddTechPanel
          tenantId={tenantId}
          roles={packJobRoles}
          onDone={() => setTeamVersion((v) => v + 1)}
          onClose={() => setDoor('none')}
        />
      ) : null}
      {door === 'invite' ? (
        <InvitePanel
          tenantId={tenantId}
          roles={crewRoles}
          onInvited={() => setTeamVersion((v) => v + 1)}
          onClose={() => setDoor('none')}
        />
      ) : null}

      {pending.length > 0 ? (
        <div class="techs__pending" data-testid="techs-pending">
          <div class="techs__eyebrow">
            {pending.length === 1 ? '1 invite pending' : `${pending.length} invites pending`}
          </div>
          {pending.map((p) => (
            <div class="techs__pending-row" key={p.id}>
              <span class="techs__pending-who">{p.name ?? p.email ?? 'Someone'}</span>
              <span class="techs__pending-as">
                {p.jobRoles.length > 0 ? p.jobRoles.join(' · ') : p.role}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {todayFailure !== null ? (
        // The roster below is real; what went unread is how many jobs each
        // of them has today. A '—' with no sentence beside it reads as zero.
        <div class="techs__unread" role="status">
          <b>{todayFailure.headline}</b> {todayFailure.detail} The team below is real — it is today's
          count that went unread.
          {todayFailure.retryable ? (
            <>
              {' '}
              <button
                type="button"
                class="techs__retry techs__retry--inline"
                onClick={() => setRetryNonce((n) => n + 1)}
              >
                Read it again
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {active.length === 0 ? (
        <div class="techs__empty">No technicians yet.</div>
      ) : (
        <div class="techs__list">
          {active.map((t) => {
            const today = todayByTech?.get(t.id);
            const sub = [textField(t, 'role'), textField(t, 'phone')].filter(Boolean).join(' · ');
            // S5d §3 — the Telegram door on the roster row, for a role that
            // can complete it. The membership bound to this record decides
            // what the line says; no binding, no line.
            const bound = editable ? memberByRecord.get(t.id) : undefined;
            const enrolment = bound && bound.status === 'active' ? enrolments.get(bound.id) : undefined;
            return (
              <div class="techs__item" key={t.id}>
                <button
                  type="button"
                  class="techs__row"
                  onClick={() => onNavigate(routePath({ screen: 'techDetail', id: t.id }))}
                >
                  <span class="techs__stripe" />
                  <span class="techs__main">
                    <b>{t.display_name}</b>
                    {sub ? <small>{sub}</small> : null}
                  </span>
                  <span class="techs__right">
                    {todayByTech !== null
                      ? `${today ?? 0} today`
                      : todayFailure !== null
                        ? 'not read'
                        : 'reading…'}
                  </span>
                </button>
                {bound === undefined ? null : bound.status === 'invited' ? (
                  // The door says why it waits rather than hiding: an
                  // invited-not-yet-accepted membership cannot be enrolled.
                  <div class="techs__enrol" data-testid={`techs-enrol-${t.id}`}>
                    Telegram · waits until they accept their invite
                  </div>
                ) : enrolment === undefined ? null : enrolment === 'unread' ? (
                  <div class="techs__enrol" data-testid={`techs-enrol-${t.id}`}>
                    Telegram · not read
                  </div>
                ) : enrolment === null ? (
                  <div class="techs__enrol" data-testid={`techs-enrol-${t.id}`}>
                    {enrolOpenFor === t.id ? (
                      <span class="techs__enrol-form">
                        <input
                          class="techs__enrol-phone"
                          type="tel"
                          value={enrolPhone}
                          aria-label={`Telegram phone for ${t.display_name}`}
                          data-testid={`techs-enrol-phone-${t.id}`}
                          disabled={enrolBusy}
                          onInput={(e) => setEnrolPhone((e.currentTarget as HTMLInputElement).value)}
                        />
                        <button
                          type="button"
                          class="techs__enrol-go"
                          data-testid={`techs-enrol-send-${t.id}`}
                          disabled={enrolPhone.trim().length === 0 || enrolBusy}
                          onClick={() => void submitEnrol(bound.id)}
                        >
                          {enrolBusy ? 'Connecting…' : 'Connect'}
                        </button>
                        <button
                          type="button"
                          class="techs__enrol-cancel"
                          disabled={enrolBusy}
                          onClick={() => {
                            setEnrolOpenFor(null);
                            setEnrolError(null);
                          }}
                        >
                          Cancel
                        </button>
                        {enrolError !== null ? (
                          <span class="techs__enrol-error" role="alert">
                            {enrolError}
                          </span>
                        ) : null}
                      </span>
                    ) : (
                      <button
                        type="button"
                        class="techs__enrol-open"
                        data-testid={`techs-enrol-open-${t.id}`}
                        onClick={() => {
                          setEnrolOpenFor(t.id);
                          // Pre-filled from the profile record where a phone
                          // is bound — the admin corrects it to E.164 if the
                          // record's copy isn't; the server's message teaches.
                          setEnrolPhone(textField(t, 'phone'));
                          setEnrolError(null);
                        }}
                      >
                        Connect to Telegram
                      </button>
                    )}
                  </div>
                ) : (
                  // Pending shown honestly: the last step is THEIRS — the
                  // bot binds when they share their number from their phone.
                  // Ink, never amber: waiting is not a warning.
                  <div class="techs__enrol" data-testid={`techs-enrol-${t.id}`}>
                    {enrolment.status === 'bound'
                      ? `Telegram · connected · ${enrolment.phoneMasked}`
                      : `Telegram · ${enrolment.phoneMasked} · waiting for them to share their number`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {retired.length > 0 ? (
        <>
          <button
            type="button"
            class="techs__retired-toggle"
            aria-expanded={showRetired}
            onClick={() => setShowRetired((v) => !v)}
          >
            {showRetired ? '– ' : '+ '}
            {retired.length === 1 ? '1 retired' : `${retired.length} retired`}
            {showRetired ? ' ▴' : ' ▾'}
          </button>
          {showRetired ? (
            <div class="techs__list techs__list--retired">
              {retired.map((t) => (
                <button
                  type="button"
                  class="techs__row"
                  key={t.id}
                  onClick={() => onNavigate(routePath({ screen: 'techDetail', id: t.id }))}
                >
                  <span class="techs__stripe" />
                  <span class="techs__main">
                    <b>{t.display_name}</b>
                    <small>{[textField(t, 'role'), 'Retired'].filter(Boolean).join(' · ')}</small>
                  </span>
                  <span class="techs__right">—</span>
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {!editable ? (
        <div class="techs__askline">
          <span class="techs__askline-stripe" />
          <span>
            <b>Read only here.</b> Scheduling is yours; pay, tickets and status belong to the back
            office. Anything you change becomes a request to them.
          </span>
        </div>
      ) : null}
    </div>
  );
}
